import { useState } from 'react';
import { Box, Typography, ClickAwayListener, Tooltip, IconButton, Chip, useTheme, darken } from '@mui/material';
import { MdInfoOutline } from 'react-icons/md';
import CopyIcon from 'assets/icons/CopyIcon';
import { getUrlInfo } from '@/utils/helperFunctions';

const GOOGLE_OAUTH_SCOPES = ['calendar.readonly', 'calendar.events'];

export default function GoogleOAuthConfigInfo() {
  const theme = useTheme();
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
  const { protocol, domain } = getUrlInfo();
  const redirectUri = `${protocol}//api-${domain}/oauth/google/callback`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(redirectUri);
    setCopyTooltipOpen(true);
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.primary.light,
        border: `1px solid ${darken(theme.palette.primary.light, 0.1)}`,
        borderRadius: 2,
        p: { xs: 1.5, sm: 2 }
      }}
    >
      <Box display='flex' flexDirection='row' alignItems='center' gap={1} mb={1}>
        <Box
          component='span'
          sx={{
            width: { xs: 20, sm: 24 },
            height: { xs: 20, sm: 24 },
            color: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <MdInfoOutline size={18} />
        </Box>
        <Typography
          fontWeight={500}
          color={theme.palette.primary.main}
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          Google OAuth Configuration
        </Typography>
      </Box>
      <Typography color={theme.palette.primary.main} sx={{ fontSize: { xs: '12px', sm: '14px' } }} mb={1}>
        To complete your Google integration, add this redirect URI in your Google Cloud Console under OAuth 2.0 Client
        IDs:
      </Typography>

      <Box
        display='flex'
        flexDirection={{ xs: 'column', sm: 'row' }}
        borderRadius={1}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={{ xs: 1, sm: 0 }}
        sx={{ backgroundColor: 'white', px: { xs: 1, sm: 1.5 } }}
      >
        <Box
          display='flex'
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={{ xs: 0.5, sm: '3px' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}
        >
          <Typography
            color={theme.palette.primary.main}
            fontWeight={600}
            sx={{ fontSize: { xs: '12px', sm: '14px' } }}
            noWrap
          >
            Redirect URI:
          </Typography>
          <Typography
            rel='noopener noreferrer'
            sx={{
              fontSize: { xs: '12px', sm: '14px' },
              color: theme.palette.primary.main,
              wordBreak: 'break-all',
              textDecoration: 'underline'
            }}
          >
            {redirectUri}
          </Typography>
        </Box>

        <ClickAwayListener onClickAway={() => setCopyTooltipOpen(false)}>
          <Tooltip
            onClose={() => setCopyTooltipOpen(false)}
            open={copyTooltipOpen}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            placement='top'
            title='Copied'
          >
            <IconButton onClick={handleCopy} size='small' sx={{ flexShrink: 0 }}>
              <Box color={theme.palette.primary.main}>
                <CopyIcon />
              </Box>
            </IconButton>
          </Tooltip>
        </ClickAwayListener>
      </Box>

      <Typography mt={1} color={theme.palette.primary.main} sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
        Make sure to enable the following scopes:
      </Typography>
      <Box display='flex' flexWrap='wrap' mt={1} gap={1}>
        {GOOGLE_OAUTH_SCOPES.map((scope) => (
          <Chip
            key={scope}
            label={scope}
            size='small'
            sx={{
              border: `1px solid ${theme.palette.primary.main}`,
              bgcolor: 'transparent',
              color: theme.palette.primary.main,
              borderRadius: '40px',
              textTransform: 'none'
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
